import React from "react";
import ArticleLayout from "./ArticleLayout";
import ProtectedEmail from "@/components/ProtectedEmail";

const LAST_UPDATED = "24 February 2026";
const COMPANY = "Nickolas Hobbs-Ainley, sole trader trading as AgentReply";
const COMPANY_SHORT = "AgentReply";
const OPERATOR_NAME = "Nickolas Hobbs-Ainley";
const OPERATOR_COUNTRY = "Spain";
const OPERATOR_NIF = "X3502016J";
const DOMAIN = "agentreply.io";

const LegalNav = () => (
  <div className="flex flex-wrap gap-x-6 gap-y-2 mb-10 pb-6 border-b border-[#F5F2EB]/10 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">
    <a href="/legal/privacy" className="hover:text-[#D95B38] transition-colors">Privacy</a>
    <a href="/legal/terms" className="hover:text-[#D95B38] transition-colors">Terms</a>
    <a href="/legal/cookies" className="hover:text-[#D95B38] transition-colors">Cookies</a>
    <a href="/legal/gdpr" className="hover:text-[#D95B38] transition-colors">GDPR</a>
  </div>
);

const Heading = ({ children }) => (
  <h2 className="font-serif text-2xl mt-12 mb-4 text-[#F5F2EB]">{children}</h2>
);
const P = ({ children }) => (
  <p className="font-mono text-sm text-[#E0D8CA] leading-relaxed mb-4">{children}</p>
);
const Bul = ({ children }) => (
  <ul className="font-mono text-sm text-[#E0D8CA] leading-relaxed mb-4 space-y-2 list-disc pl-6">{children}</ul>
);
const DraftBanner = () => (
  <div className="bg-[#D95B38]/10 border border-[#D95B38] px-5 py-4 mb-10 text-[#E0D8CA] font-mono text-xs">
    <span className="text-[#D95B38] uppercase tracking-[0.25em] mr-2">Draft</span>
    This page is a baseline template. Please have a qualified lawyer review it before relying on it for production.
  </div>
);

// ============================================================
//  PRIVACY POLICY
// ============================================================
export function PrivacyPolicy() {
  return (
    <ArticleLayout
      kicker={`Last updated: ${LAST_UPDATED}`}
      title="Privacy Policy."
      intro={`How ${COMPANY} ("AgentReply", "we", "us") collects, uses, and protects your information.`}
      backToHelp={false}
    >
      <DraftBanner />
      <LegalNav />

      <Heading>1. Who we are</Heading>
      <P>
        {DOMAIN} is operated by <strong className="text-[#F5F2EB]">{OPERATOR_NAME}</strong>, a sole trader (<em>autónomo</em>) registered in {OPERATOR_COUNTRY}, NIE <strong className="text-[#F5F2EB]">{OPERATOR_NIF}</strong>, trading as <strong className="text-[#F5F2EB]">{COMPANY_SHORT}</strong> ("AgentReply", "we", "us"). AgentReply is a tool that lets businesses build AI agents that reply to customers on WhatsApp, the web, and embedded chat widgets. You can contact us at <ProtectedEmail />.
      </P>
      <P>
        For the purposes of GDPR / UK GDPR, the data controller of your account information is {OPERATOR_NAME} (NIE {OPERATOR_NIF}), contactable at <ProtectedEmail />. Registered business address available on request.
      </P>

      <Heading>2. What we collect</Heading>
      <P>We collect three categories of information:</P>
      <Bul>
        <li><strong className="text-[#F5F2EB]">Account data</strong> — name, email address, hashed password (or Google profile if you sign in with Google). Stored to authenticate you.</li>
        <li><strong className="text-[#F5F2EB]">Customer-facing data</strong> — content of agent prompts, uploaded knowledge documents, contact records and conversation messages you collect via your agents.</li>
        <li><strong className="text-[#F5F2EB]">Usage data</strong> — IP address, user-agent, timestamps of API calls, rate-limit events. Used for security and abuse prevention.</li>
      </Bul>
      <P>We do <strong>not</strong> collect payment card details — those are handled directly by Stripe (see "Sub-processors" below).</P>

      <Heading>3. Why we collect it (legal basis under GDPR)</Heading>
      <Bul>
        <li><strong className="text-[#F5F2EB]">Contract</strong> — to provide the service you signed up for.</li>
        <li><strong className="text-[#F5F2EB]">Legitimate interests</strong> — security, fraud prevention, service improvement.</li>
        <li><strong className="text-[#F5F2EB]">Consent</strong> — for optional marketing communications (you can withdraw any time).</li>
        <li><strong className="text-[#F5F2EB]">Legal obligation</strong> — to respond to lawful authority requests.</li>
      </Bul>

      <Heading>4. How long we keep it</Heading>
      <Bul>
        <li>Account data — for as long as your account is active, plus up to 90 days after deletion for tax/audit reasons.</li>
        <li>Conversation messages — kept until you delete them or close your account.</li>
        <li>Usage logs — 90 days.</li>
        <li>Stripe payment records — 7 years (tax requirement).</li>
      </Bul>

      <Heading>5. Sub-processors (companies that help us run the service)</Heading>
      <Bul>
        <li><strong className="text-[#F5F2EB]">Anthropic (Claude)</strong> — generates AI replies. Messages sent to Claude are <em>not</em> used to train its models.</li>
        <li><strong className="text-[#F5F2EB]">OpenAI</strong> — speech-to-text (Whisper) and text-to-speech (TTS). Audio is processed in flight, not retained for training.</li>
        <li><strong className="text-[#F5F2EB]">ElevenLabs</strong> — premium text-to-speech and voice cloning. Voice samples you upload are stored against your ElevenLabs voice profile, used only to synthesise replies on your behalf.</li>
        <li><strong className="text-[#F5F2EB]">Stripe</strong> — payment processing (US, EU, UK).</li>
        <li><strong className="text-[#F5F2EB]">Twilio</strong> — WhatsApp message delivery (when you connect Twilio).</li>
        <li><strong className="text-[#F5F2EB]">Meta (WhatsApp Business / Messenger / Instagram)</strong> — message routing where you connect those channels directly.</li>
        <li><strong className="text-[#F5F2EB]">MongoDB Atlas</strong> — database hosting (encrypted at rest).</li>
        <li><strong className="text-[#F5F2EB]">HostGator</strong> — transactional email (verification, password reset).</li>
      </Bul>
      <P>All sub-processors are bound by data processing agreements that meet GDPR Article 28 requirements. The full, up-to-date list is published here and notified to customers 30 days before any addition.</P>

      <Heading>6. International transfers</Heading>
      <P>
        Some sub-processors are based outside the EU/EEA (notably Anthropic, OpenAI, and Stripe in the US). Transfers are protected by the EU Standard Contractual Clauses and the EU–US Data Privacy Framework where applicable.
      </P>

      <Heading>7. How AI processing works</Heading>
      <P>
        When you or your customers send a message to an agent, the message text (plus relevant retrieved knowledge-base snippets and the agent's persona / system prompt) is sent to our AI sub-processors (Anthropic and/or OpenAI) to generate a reply. Audio voice notes are transcribed by OpenAI Whisper before being passed through the same flow.
      </P>
      <Bul>
        <li>Messages are processed in flight — they are <strong>not</strong> used by Anthropic or OpenAI to train their foundation models (per our enterprise data-processing terms with them).</li>
        <li>We retain the message and the AI's reply in our database to power conversation history, contact memory, and analytics.</li>
        <li>If you enable voice replies, generated audio is temporarily cached (up to 5 minutes) so the delivery network (Twilio) can fetch it, then purged automatically.</li>
        <li>If you upload an audio sample for voice cloning, it is sent to ElevenLabs to create a voice model under your account. We retain a copy of the sample only as required to provide the cloned voice.</li>
      </Bul>
      <P>
        We don't use your customers' messages to train models — ours or third-party — under any circumstances.
      </P>

      <Heading>8. Your rights (GDPR & UK GDPR)</Heading>
      <P>You have the right to:</P>
      <Bul>
        <li>Access the personal data we hold about you.</li>
        <li>Correct inaccurate data.</li>
        <li>Delete your data ("right to be forgotten").</li>
        <li>Export your data in a machine-readable format.</li>
        <li>Object to or restrict certain processing.</li>
        <li>Lodge a complaint with your local data protection authority — in Spain this is the <a href="https://www.aepd.es/" target="_blank" rel="noopener noreferrer" className="text-[#D95B38] hover:underline">Agencia Española de Protección de Datos (AEPD)</a>; UK residents may complain to the <a href="https://ico.org.uk/" target="_blank" rel="noopener noreferrer" className="text-[#D95B38] hover:underline">ICO</a>.</li>
      </Bul>
      <P>To exercise any of these, email <ProtectedEmail />. We respond within 30 days.</P>

      <Heading>9. Security</Heading>
      <P>
        We use HTTPS everywhere, bcrypt password hashing, encrypted database storage, environment-isolated credentials, rate limiting and brute-force lockout. We don't store payment card details — Stripe handles that under PCI-DSS Level 1.
      </P>

      <Heading>10. Children's privacy</Heading>
      <P>AgentReply is not intended for users under 16. We don't knowingly collect data from children.</P>

      <Heading>11. Changes to this policy</Heading>
      <P>
        We'll email registered users at least 30 days before any material change takes effect. The "Last updated" date at the top of this page is always the source of truth.
      </P>

      <Heading>12. Contact</Heading>
      <P>
        Questions, requests, or complaints: <ProtectedEmail />.
      </P>
    </ArticleLayout>
  );
}

// ============================================================
//  TERMS OF SERVICE
// ============================================================
export function TermsOfService() {
  return (
    <ArticleLayout
      kicker={`Last updated: ${LAST_UPDATED}`}
      title="Terms of Service."
      intro={`The agreement between you and ${COMPANY} when you use AgentReply.`}
      backToHelp={false}
    >
      <DraftBanner />
      <LegalNav />

      <Heading>1. The agreement</Heading>
      <P>By creating an account or using {DOMAIN}, you agree to these Terms with <strong className="text-[#F5F2EB]">{OPERATOR_NAME}</strong>, a sole trader (<em>autónomo</em>) registered in {OPERATOR_COUNTRY}, NIE <strong className="text-[#F5F2EB]">{OPERATOR_NIF}</strong>, trading as <strong className="text-[#F5F2EB]">{COMPANY_SHORT}</strong> ("AgentReply", "we", "us"). If you don't agree, please don't use the service.</P>

      <Heading>2. The service</Heading>
      <P>
        AgentReply provides software-as-a-service tools that let you create AI agents to reply to customer messages on WhatsApp, public chat links, and embedded widgets. We do <em>not</em> guarantee the AI's answers are accurate, complete, or appropriate — you remain responsible for what your agents say.
      </P>

      <Heading>3. Your account</Heading>
      <Bul>
        <li>You must be at least 16 years old.</li>
        <li>Keep your password confidential. You're responsible for activity on your account.</li>
        <li>One account per individual or business entity.</li>
        <li>Tell us immediately if you suspect unauthorised access.</li>
      </Bul>

      <Heading>4. Acceptable use</Heading>
      <P>You agree NOT to use AgentReply to:</P>
      <Bul>
        <li>Send spam, scams, phishing, or unsolicited bulk messages.</li>
        <li>Impersonate any person or entity.</li>
        <li>Distribute malware or attempt to compromise our infrastructure.</li>
        <li>Generate content that is illegal, defamatory, sexually explicit involving minors, or hateful.</li>
        <li>Bypass rate limits or attempt to reverse-engineer the service.</li>
        <li>Violate WhatsApp's, Stripe's, Anthropic's or OpenAI's terms of service.</li>
      </Bul>
      <P>We may suspend or terminate accounts that breach these rules with no refund.</P>

      <Heading>5. Subscription, payments, and refunds</Heading>
      <Bul>
        <li>14-day free trial, no credit card required.</li>
        <li>Monthly ($9.99), Yearly ($70), and Lifetime ($250) plans, all in USD, billed via Stripe.</li>
        <li>Monthly and yearly plans renew automatically until cancelled.</li>
        <li>14-day full-refund window on monthly/yearly purchases — email us.</li>
        <li>Lifetime plans are refundable within 30 days of purchase only.</li>
        <li>Local sales tax / VAT may apply depending on your country.</li>
      </Bul>

      <Heading>6. Your content</Heading>
      <P>
        You own all content you upload or generate through AgentReply (prompts, knowledge documents, conversation history). You grant us a worldwide, non-exclusive licence to host and process that content solely to provide the service.
      </P>

      <Heading>7. AI output disclaimer</Heading>
      <P>
        AI agents can make mistakes. They may produce inaccurate, biased, or inappropriate responses. You acknowledge that AgentReply is a tool to assist customer communication — not a replacement for human judgement on important matters (legal, medical, financial advice, etc.). You are solely responsible for what your agents tell your customers.
      </P>

      <Heading>7a. AI usage rules</Heading>
      <P>In addition to section 4, when using the AI features of AgentReply you agree NOT to:</P>
      <Bul>
        <li>Use generated voices or text to impersonate real individuals without their explicit, documented consent (including deepfake-style voice cloning).</li>
        <li>Generate content depicting minors in sexual contexts, instructions for violence or self-harm, or any unlawful material.</li>
        <li>Use the AI to make legally-binding decisions on behalf of your customers (e.g. medical diagnoses, financial advice, immigration determinations) without a qualified human in the loop.</li>
        <li>Resell, repackage, or expose raw model outputs as a competing AI API.</li>
        <li>Attempt to extract our system prompts, jailbreak the model's safety guardrails, or use the API for adversarial testing of other providers' systems.</li>
      </Bul>
      <P>
        If you clone a voice (yours or another person's), you confirm you have explicit written permission from the speaker. You indemnify {COMPANY} against any claim arising from misuse of cloned voices on your account.
      </P>

      <Heading>8. Third-party services</Heading>
      <P>
        WhatsApp messaging, payments, and AI generation are performed by Twilio, Stripe, Anthropic, and OpenAI respectively. Their terms apply to those parts of the service. We are not liable for outages or changes at these providers.
      </P>

      <Heading>9. Cancellation and termination</Heading>
      <P>
        You can cancel any time by emailing <ProtectedEmail />. We may suspend the service for breach of these Terms with reasonable notice (or immediately for serious violations). On termination, you can export your data for up to 30 days.
      </P>

      <Heading>10. Limitation of liability</Heading>
      <P>
        To the maximum extent permitted by law, {COMPANY}'s total liability arising out of or in connection with these Terms is limited to the amount you paid us in the 12 months preceding the claim. We are not liable for indirect, incidental, consequential, or punitive damages.
      </P>

      <Heading>11. Indemnity</Heading>
      <P>
        You agree to indemnify and hold us harmless from any claims arising out of content your agents send, your use of the service in breach of these Terms, or your violation of any third party's rights.
      </P>

      <Heading>12. Changes</Heading>
      <P>We may update these Terms. Material changes take effect 30 days after we notify registered users by email.</P>

      <Heading>13. Governing law</Heading>
      <P>
        These Terms are governed by the laws of <strong className="text-[#F5F2EB]">Spain</strong>. Disputes are subject to the exclusive jurisdiction of the competent Spanish courts. If you are a consumer in another EU/EEA country, this clause does not deprive you of the protection afforded by the mandatory consumer-protection laws of your country of residence.
      </P>

      <Heading>14. Contact</Heading>
      <P>Questions about these Terms: <ProtectedEmail />.</P>
    </ArticleLayout>
  );
}

// ============================================================
//  COOKIE POLICY
// ============================================================
export function CookiePolicy() {
  return (
    <ArticleLayout
      kicker={`Last updated: ${LAST_UPDATED}`}
      title="Cookie Policy."
      intro={`How ${DOMAIN} uses cookies and similar technologies.`}
      backToHelp={false}
    >
      <DraftBanner />
      <LegalNav />

      <Heading>1. What are cookies and similar technologies?</Heading>
      <P>
        Cookies are small text files stored on your device when you visit a website. Similar technologies covered by this policy include localStorage, sessionStorage, web beacons / pixels, and any other tracking technologies that read or write data to your browser. They help the site remember your preferences and keep you signed in.
      </P>

      <Heading>2. What we use</Heading>
      <P>We use only <strong>strictly necessary</strong> cookies. <strong>We do not run third-party analytics or marketing trackers</strong> — meaning no Google Analytics, no Meta Pixel, no Hotjar, no TikTok Pixel, no LinkedIn Insight Tag, and no advertising re-targeting on our own pages.</P>
      <Bul>
        <li>
          <strong className="text-[#F5F2EB]">session_token</strong> (httpOnly, secure, 7 days) — keeps you logged in. Without it you'd have to sign in on every page.
        </li>
      </Bul>
      <P>
        If we ever add analytics or marketing trackers in the future, we'll show a consent banner first and update this policy.
      </P>

      <Heading>3. localStorage (technically not a cookie, but covered here)</Heading>
      <P>The public chat page and embed widget use your browser's localStorage to:</P>
      <Bul>
        <li>Remember an anonymous visitor ID so the agent recognises you across visits.</li>
        <li>Cache your name, email, and phone (only if you provided them) so you don't have to re-enter them.</li>
      </Bul>
      <P>This data never leaves your browser — it's not sent to our servers as a cookie. You can clear it any time from your browser settings.</P>

      <Heading>4. Cookies set by sub-processors (during specific actions)</Heading>
      <P>The following cookies are set only when you actively use a feature operated by a third party:</P>
      <Bul>
        <li><strong className="text-[#F5F2EB]">Stripe</strong> — when you click "Subscribe" / "Buy" and reach Stripe Checkout, Stripe sets its own cookies on its checkout.stripe.com domain for fraud prevention. See <a href="https://stripe.com/cookies-policy" target="_blank" rel="noopener noreferrer" className="text-[#D95B38] hover:underline">Stripe's cookie policy</a>.</li>
        <li><strong className="text-[#F5F2EB]">Google</strong> — only if you click "Continue with Google" to sign in. Cookies are set on Google's auth domain (accounts.google.com), not on agentreply.io.</li>
        <li><strong className="text-[#F5F2EB]">Twilio</strong> — none on our pages. Twilio only processes the WhatsApp messages your customers send; it does not set browser cookies on agentreply.io.</li>
      </Bul>
      <P>
        We don't control cookies set on third-party domains. To opt out of those, manage your preferences on the respective provider's site.
      </P>

      <Heading>5. Your choices</Heading>
      <P>
        Because we only use strictly-necessary cookies, no consent banner is required under the GDPR / UK PECR. You can still disable cookies in your browser settings, but you won't be able to sign in if you do.
      </P>

      <Heading>6. Contact</Heading>
      <P>Questions: <ProtectedEmail />.</P>
    </ArticleLayout>
  );
}

// ============================================================
//  GDPR / DATA PROCESSING
// ============================================================
export function GDPRPage() {
  return (
    <ArticleLayout
      kicker={`Last updated: ${LAST_UPDATED}`}
      title="GDPR & Data Processing."
      intro={`How AgentReply complies with the EU and UK General Data Protection Regulations.`}
      backToHelp={false}
    >
      <DraftBanner />
      <LegalNav />

      <Heading>1. Roles</Heading>
      <P>
        For the personal data of <strong>your end customers</strong> (people chatting with your agents), <strong className="text-[#F5F2EB]">you are the data controller</strong> and <strong className="text-[#F5F2EB]">we are the data processor</strong>.
      </P>
      <P>
        For your own account data (your name, email, password), <strong className="text-[#F5F2EB]">we are the data controller</strong>.
      </P>

      <Heading>2. Data Processing Agreement (DPA)</Heading>
      <P>
        By signing up to AgentReply, you accept our DPA which forms part of these Terms. It includes the EU Standard Contractual Clauses for any transfer outside the EU/EEA.
      </P>
      <P>If you need a signed/countersigned DPA copy for your records, email <ProtectedEmail /> and we'll send one within 5 business days.</P>

      <Heading>3. What we process on your behalf</Heading>
      <Bul>
        <li>Names, emails, phone numbers your customers provide via intake forms.</li>
        <li>Conversation messages between customers and your agents.</li>
        <li>Documents you upload to the knowledge base.</li>
        <li>IP addresses and timestamps (for rate-limiting and abuse prevention).</li>
      </Bul>

      <Heading>4. Sub-processors</Heading>
      <P>See section 5 of our <a href="/legal/privacy" className="text-[#D95B38] hover:underline">Privacy Policy</a> for the full list. By using AgentReply you authorise us to engage these sub-processors. We'll give 30 days' notice before adding new ones, and you can object by terminating your subscription.</P>

      <Heading>5. International transfers</Heading>
      <P>
        Some sub-processors are based in the US (Anthropic, OpenAI, Stripe). Transfers are covered by the EU Standard Contractual Clauses and the EU–US Data Privacy Framework. Adequacy decisions exist for the UK and Switzerland.
      </P>

      <Heading>6. Security measures</Heading>
      <Bul>
        <li>HTTPS encryption in transit (TLS 1.2+).</li>
        <li>AES-256 encryption at rest (MongoDB Atlas).</li>
        <li>Bcrypt password hashing.</li>
        <li>Role-based access controls — only authorised engineers can access production data, with audit logs.</li>
        <li>Rate limiting and brute-force lockout on all auth endpoints.</li>
        <li>Regular dependency updates and security patches.</li>
      </Bul>

      <Heading>7. Data subject requests</Heading>
      <P>If one of your customers asks YOU (the controller) to access, correct, or delete their data:</P>
      <Bul>
        <li>Certain deletion and export functions may be available through your AgentReply dashboard (Contacts tab). Availability of self-service tools varies and continues to expand.</li>
        <li>For anything more complex — or if a self-service option isn't available — email <ProtectedEmail /> with the request details and we'll help within 30 days.</li>
      </Bul>

      <Heading>8. Data breach notification</Heading>
      <P>
        If we become aware of a personal-data breach affecting your customers, we'll notify you without undue delay (and in any event within 72 hours) with the information you need to comply with your own GDPR Article 33 notification obligations.
      </P>

      <Heading>9. Data retention and deletion</Heading>
      <P>
        On termination of your subscription, we delete all customer data within 30 days unless you request a longer retention window in writing. Backups are purged within 90 days.
      </P>

      <Heading>10. Data Protection Officer</Heading>
      <P>
        AgentReply is operated by {OPERATOR_NAME} (NIE <strong className="text-[#F5F2EB]">{OPERATOR_NIF}</strong>), a sole trader registered as <em>autónomo</em> in {OPERATOR_COUNTRY}. As a small business, we are not legally required to appoint a formal Data Protection Officer under Article 37 GDPR / UK GDPR.
      </P>
      <P>
        However, privacy enquiries, data-subject requests, and breach reports can be directed to our designated privacy contact at <ProtectedEmail />. Registered business address available on request.
      </P>
    </ArticleLayout>
  );
}
